import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Search, MapPin } from 'lucide-react'

const pinIcon = L.divIcon({
  className: '',
  html: '<div style="width:16px;height:16px;border-radius:50%;background:#06b6d4;border:3px solid white;box-shadow:0 0 0 2px rgba(6,182,212,0.4)"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
})

export default function LocationPicker({ lat, lon, onChange }) {
  const mapRef = useRef(null)
  const mapInstance = useRef(null)
  const markerRef = useRef(null)
  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')

  useEffect(() => {
    if (mapInstance.current || !mapRef.current) return

    const map = L.map(mapRef.current, { attributionControl: false }).setView([lat, lon], 10)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
    }).addTo(map)
    L.control.attribution({ prefix: false }).addAttribution('© OpenStreetMap').addTo(map)

    const marker = L.marker([lat, lon], { icon: pinIcon, draggable: true }).addTo(map)
    marker.on('dragend', () => {
      const pos = marker.getLatLng()
      onChange(pos.lat, pos.lng)
    })
    map.on('click', (e) => {
      marker.setLatLng(e.latlng)
      onChange(e.latlng.lat, e.latlng.lng)
    })

    mapInstance.current = map
    markerRef.current = marker

    setTimeout(() => map.invalidateSize(), 100)

    return () => { map.remove(); mapInstance.current = null }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (markerRef.current && mapInstance.current) {
      const current = markerRef.current.getLatLng()
      if (Math.abs(current.lat - lat) > 0.0001 || Math.abs(current.lng - lon) > 0.0001) {
        markerRef.current.setLatLng([lat, lon])
        mapInstance.current.setView([lat, lon], mapInstance.current.getZoom())
      }
    }
  }, [lat, lon])

  const handleSearch = async (e) => {
    e.preventDefault()
    if (!query.trim()) return
    setSearching(true)
    setSearchError('')
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`)
      const results = await res.json()
      if (results.length === 0) {
        setSearchError('Location not found')
        return
      }
      const { lat: foundLat, lon: foundLon } = results[0]
      onChange(parseFloat(foundLat), parseFloat(foundLon))
    } catch {
      setSearchError('Search failed')
    } finally {
      setSearching(false)
    }
  }

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSearch(e) }}
          placeholder="Search for a city or address..."
          className="flex-1 px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-flight-cyan focus:outline-none"
        />
        <button type="button" onClick={handleSearch} disabled={searching} className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:text-white disabled:opacity-50">
          {searching ? <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" /> : <Search className="w-4 h-4" />}
        </button>
      </div>
      {searchError && <div className="text-red-400 text-xs mb-2">{searchError}</div>}
      <div ref={mapRef} className="w-full h-64 rounded-lg overflow-hidden border border-slate-700" />
      <div className="flex items-center gap-1.5 mt-2 text-slate-400 text-xs">
        <MapPin className="w-3 h-3" /> {lat.toFixed(4)}°, {lon.toFixed(4)}° (click or drag the pin to fine-tune)
      </div>
    </div>
  )
}
